import { useState } from "react";
import { useIntl } from "react-intl";
import Card from "./card";
import { Asset } from "../../../models/asset";
import { Scheduling } from "../../../models/schedule";
import { Loading } from "../../../utils/loading";
import { EmptyNextSchedules } from "./emptySchedules";
import { useRef } from 'react';
import { useReactToPrint } from "react-to-print";
import PeriodicReviewsPdf from "./periodicReviewsPdf";
import Html2Pdf from "js-html2pdf";
import { SignedRevision } from "../../../models/signedRevision";
import { getChassisData } from "../../../data/chassisReviewData";
import { useMediaQuery } from "@mui/material";

interface ICheckupsProps {
  asset: Asset | null;
  blockchainData: (scheduleId: string) => SignedRevision | undefined;
  loading: boolean;
  isEnhanced?: boolean;
  userRole?: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP';
  hiddenIds: Set<string>;
  setHiddenIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const Checkups = ({ asset, blockchainData, loading, isEnhanced, userRole, hiddenIds, setHiddenIds }: ICheckupsProps) => {
  const intl = useIntl();
  const contentRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const isResponsiveView = useMediaQuery("(max-width: 1023px)");

  /**
     * Divide uma array de Scheduling em subarrays de até 4 elementos cada.
     * @param schedulings Array de Scheduling
     * @returns Array de arrays, cada um com até 4 Scheduling
     */
  function paginateSchedulings(schedulings: Scheduling[]): Scheduling[][] {
    const result: Scheduling[][] = [];
    for (let i = 0; i < schedulings.length; i += 4) {
      result.push(schedulings.slice(i, i + 4));
    }
    return result;
  }

  const downloadPeriodicReviews = useReactToPrint({
    contentRef,
    documentTitle: intl.formatMessage({ id: "intl-msg:periodicReviews.maintenanceAndReviews" }),
    print: async (printIframe) => {
      setDownloading(true);
      const document = printIframe.contentDocument;
      if (document) {
        try {
          const html = document.getElementById("contentRef");
          const exporter = new Html2Pdf(
            html,
            {
              filename: `${intl.formatMessage({ id: "intl-msg:periodicReviews.maintenanceAndReviews" })} - ${asset?.name}.pdf`,
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: { scale: 2, logging: true, dpi: 192, letterRendering: true },
              jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
              pagebreak: { mode: 'css', before: '.page-break' }
            }
          );
          // Generate PDF
          const pdf = await exporter.getPdf(false);

          // Remove last page (blank page) if it exists
          const allSchedulingsForCount = [
            ...(asset?.schedulings?.previous ?? []),
            ...(asset?.schedulings?.next ?? []),
          ];
          const mainApiNamesLowerForCount = ["entrega técnica", "revisão de entrega", "assentamento"];
          const schedulingRevIndexMapForCount = new Map<string, number>();
          let revCounterForCount = 0;
          allSchedulingsForCount.forEach(s => {
            if (!mainApiNamesLowerForCount.includes((s.checkup?.name || "").toLowerCase())) {
              schedulingRevIndexMapForCount.set(s.id, revCounterForCount + 2);
              revCounterForCount++;
            }
          });
          const chassis = asset?.chassis ?? "";
          const chassisData = getChassisData(chassis);
          
          const hasRevDataFn = (s: any): boolean => {
            const revIndex = schedulingRevIndexMapForCount.get(s.id);
            return revIndex !== undefined && !!chassisData?.revisions?.find(r => r.revIndex === revIndex);
          };
          
          const preBooksCountSchedulings = allSchedulingsForCount.filter(s =>
            mainApiNamesLowerForCount.includes((s.checkup?.name || "").toLowerCase())
          );
          const revisionsCountSchedulings = allSchedulingsForCount.filter(s =>
            !mainApiNamesLowerForCount.includes((s.checkup?.name || "").toLowerCase())
          );
          const revisionsWithDataCount = revisionsCountSchedulings.filter(hasRevDataFn);
          const revisionsWithoutDataCount = revisionsCountSchedulings.filter(s => !hasRevDataFn(s));
          
          const pagedPreBooksCount = Math.ceil(preBooksCountSchedulings.length / 4);
          const pagedRevisionsWithDataCount = Math.ceil(revisionsWithDataCount.length / 2);
          const pagedRevisionsWithoutDataCount = Math.ceil(revisionsWithoutDataCount.length / 4);
          const totalPageCount = pagedPreBooksCount + pagedRevisionsWithDataCount + pagedRevisionsWithoutDataCount;
          pdf.deletePage(totalPageCount + 1);

          // Open pdf in new tab
          const blob = pdf.output('blob');
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');

          // Download pdf
          pdf.save(`${intl.formatMessage({ id: "intl-msg:periodicReviews.maintenanceAndReviews" })} - ${asset?.name}.pdf`);

        } catch (error) {
          console.info(error);
        }
        setDownloading(false);
      }
    },
  });

  const [order, setOrder] = useState<string>("down");

  const handleOrderChange = () => setOrder(order === "down" ? "up" : "down");

  const extractNumber = (title: string): number => {
    const match = title.match(/\d+/);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  /**
   * Ordena os agendamentos com os 3 principais sempre em primeiro
   * Os outros são ordenados pelo número do título (1º, 2º, ... 37º)
   * @param schedulings Array de Scheduling
   * @returns Array ordenado com os 3 principais em primeiro
   */
  const sortSchedulings = (schedulings: Scheduling[]): Scheduling[] => {
    // Títulos enviados pela API sempre em pt-BR, independente do idioma da UI
    const mainApiNames = ["Entrega técnica", "Revisão de entrega", "Assentamento"];
    const mainApiNamesLower = mainApiNames.map(t => t.toLowerCase());
    
    const main = schedulings.filter(s => mainApiNamesLower.includes((s.checkup?.name || "").toLowerCase()));
    const others = schedulings.filter(s => !mainApiNamesLower.includes((s.checkup?.name || "").toLowerCase()));
    
    const sortedMain = mainApiNames
      .map(type => main.find(s => s.checkup?.name?.toLowerCase() === type.toLowerCase()))
      .filter((item): item is Scheduling => item !== undefined);
    
    const sortedOthers = others.sort((a, b) => {
      const numA = extractNumber(a.checkup?.name || "");
      const numB = extractNumber(b.checkup?.name || "");
      
      
      return order === "down" ? numA - numB : numB - numA;
    });
    
    return [...sortedMain, ...sortedOthers];
  };

  return (
    <div className="revision-content pr-5">
      {loading && <div className="flex justify-center items-center h-full">
        <Loading label={intl.formatMessage({ id: "intl-msg:periodicReviews.loadingReviews" })} />
      </div>}
      {!loading && (
        <div>
          <button
            type="button"
            className="btn btn-default mt-8 w-full sm:w-auto flex items-center justify-center sm:justify-start"
            onClick={() => {
              setDownloading(true);
              downloadPeriodicReviews();
            }}
            disabled={downloading}
          >
            {downloading ? intl.formatMessage({ id: "intl-msg:periodicReviews.downloading" }) : intl.formatMessage({ id: "intl-msg:periodicReviews.download" })}
            {!downloading && (
              <span
                className={`rioglyph rioglyph-download padding-left-10`}
                aria-hidden="true"
              />
            )}
          </button>

          <div className="flex justify-between items-center actions-row mt-10 flex-wrap">
            <div className="mb-2 sm:mb-0">{intl.formatMessage({ id: "intl-msg:periodicReviews.completedReviews" })}</div>
            <div className="form-group w-full sm:w-auto">
              <div className="display-flex gap-10 align-items-end">
                <div className="form-group">
                  <div onClick={handleOrderChange} className="cursor-pointer">
                    {order === "down" ? intl.formatMessage({ id: "intl-msg:periodicReviews.oldest" }) : intl.formatMessage({ id: "intl-msg:periodicReviews.newest" })}
                    <span className={`rioglyph rioglyph-chevron-${order} padding-left-10`} aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`grid ${isResponsiveView ? 'grid-cols-1' : 'grid-cols-2'} gap-6 scrollable mt-8`}>
            {(() => {
              const mainApiNames = ["Entrega técnica", "Revisão de entrega", "Assentamento"];
              const mainApiNamesLower = mainApiNames.map(t => t.toLowerCase());
              const allSchedulings = sortSchedulings([
                ...(asset?.schedulings?.previous ?? []),
                ...(asset?.schedulings?.next ?? []),
              ]);
              const preBook = allSchedulings.filter(s => mainApiNamesLower.includes((s.checkup?.name || "").toLowerCase()));
              const bookRevisions = allSchedulings.filter(s => !mainApiNamesLower.includes((s.checkup?.name || "").toLowerCase()));
              const visibleRevisions = bookRevisions.filter(s => !hiddenIds.has(s.checkup?.name ?? ""));

              return [
                ...preBook.map((s) => (
                  <Card key={s.id} scheduling={s} isOilChange={false} isSigned={!!blockchainData(s.id)?.id} finished={s.isFinished} isPreBook />
                )),
                ...visibleRevisions.map((s, i) => {
                  const originalIndex = bookRevisions.indexOf(s);
                  return (
                    <Card
                      key={s.checkup?.name ?? i}
                      scheduling={s}
                      isOilChange={false}
                      isSigned={!!blockchainData(s.id)?.id}
                      finished={s.isFinished}
                      isEnhanced={isEnhanced}
                      index={originalIndex}
                      onHide={() => setHiddenIds(prev => new Set([...prev, s.checkup?.name ?? ""]))}
                      userRole={userRole}
                    />
                  );
                }),
              ];
            })()}
          </div>

          {
            asset?.schedulings?.previous?.length === 0 && asset?.schedulings?.next?.length === 0 && (
              <div className="border rounded-lg p-4 w-full">
                <span className="text-size-14 text-color-dark">{intl.formatMessage({ id: "intl-msg:periodicReviews.noReviews" })}</span>
              </div>)
          }

          {asset?.schedulings?.next?.length === 0 && <EmptyNextSchedules />}

          <div className={`mt-[200px] ${!downloading ? "hidden" : ""}`}>
            <div ref={contentRef} id="contentRef">
              <PeriodicReviewsPdf
                asset={asset}
                blockchainData={blockchainData}
                order={order}
                paginateSchedulings={paginateSchedulings}
                userRole={userRole}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkups;
